// =============================================
// ConfirmRegenerateDialog — twardy confirm przed force=true regeneracja
// Pokazuje co user straci (slowa, znaki, $$, czas) zeby nie byl kolejnym "are you sure".
// =============================================

import { useEffect, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { GenerationStages } from "@/services/queueClient";

export interface RegenerateTarget {
  nodeId: string;
  nodeTitle: string;
  stages: GenerationStages;
  /** thematic_node_content row (final_html, engine_cost, engine_stage) */
  content: any;
}

interface PrevStats {
  wordCount: number;
  charCount: number;
  prevCostUsd: number | null;
  prevDurationSec: number | null;
  loading: boolean;
}

function countWords(html: string): number {
  return html.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
}

const STAGE_LABELS: Record<GenerationStages, string> = {
  structure: "strukture (Etap 1)",
  content: "tresc (Etap 2)",
  full: "caly artykul (Etap 1 + Etap 2)",
};

interface Props {
  target: RegenerateTarget | null;
  onCancel: () => void;
  onConfirm: (target: RegenerateTarget) => void | Promise<void>;
}

export function ConfirmRegenerateDialog({ target, onCancel, onConfirm }: Props) {
  const [stats, setStats] = useState<PrevStats>({
    wordCount: 0, charCount: 0, prevCostUsd: null, prevDurationSec: null, loading: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;

    (async () => {
      setStats((s) => ({ ...s, loading: true }));

      const html: string = target.content?.final_html || target.content?.draft_html || "";
      const charCount = html.length;
      const wordCount = html ? countWords(html) : 0;

      const engineCost = target.content?.engine_cost;
      const prevCostUsd = typeof engineCost?.total === "number"
        ? Number(engineCost.total)
        : (engineCost ? Object.values(engineCost).reduce((a: number, b: any) => a + (typeof b === "number" ? b : 0), 0) : null);

      // Pull most recent completed generation_queue row for duration
      let prevDurationSec: number | null = null;
      try {
        const { data: lastRun } = await (supabase as any)
          .from("generation_queue")
          .select("started_at, completed_at, result")
          .eq("node_id", target.nodeId)
          .eq("status", "completed")
          .order("completed_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (lastRun?.result?.durationMs) {
          prevDurationSec = Math.round(Number(lastRun.result.durationMs) / 1000);
        } else if (lastRun?.started_at && lastRun?.completed_at) {
          const ms = new Date(lastRun.completed_at).getTime() - new Date(lastRun.started_at).getTime();
          prevDurationSec = Math.round(ms / 1000);
        }
      } catch { /* ignore */ }

      if (!cancelled) {
        setStats({ wordCount, charCount, prevCostUsd, prevDurationSec, loading: false });
      }
    })();

    return () => { cancelled = true; };
  }, [target]);

  if (!target) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    try { await onConfirm(target); } finally { setSubmitting(false); }
  };

  const stageLabel = STAGE_LABELS[target.stages];
  const showHtmlStats = target.stages !== "structure"; // Etap 1 only nie nadpisuje html

  return (
    <AlertDialog open={!!target} onOpenChange={(open) => { if (!open && !submitting) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertTriangle className="h-5 w-5" />
            Regenerowac {stageLabel}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-2">
              <p className="text-sm text-foreground">
                <span className="font-medium">{target.nodeTitle}</span>
              </p>
              <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 space-y-1.5 text-sm">
                <p className="font-medium text-red-700 dark:text-red-400">Stracisz:</p>
                {stats.loading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Liczenie...</span>
                  </div>
                ) : (
                  <ul className="space-y-1 text-foreground">
                    {showHtmlStats && (
                      <li>
                        <span className="font-mono">{stats.charCount.toLocaleString("pl-PL")}</span> znakow
                        {stats.wordCount > 0 && (
                          <> (~<span className="font-mono">{stats.wordCount.toLocaleString("pl-PL")}</span> slow)</>
                        )}
                      </li>
                    )}
                    {target.stages !== "content" && (
                      <li className="text-muted-foreground">+ wygenerowana struktura H2/H3 zostanie nadpisana</li>
                    )}
                    {stats.prevCostUsd !== null && (
                      <li>
                        Koszt poprzedniej generacji: <span className="font-mono">${stats.prevCostUsd.toFixed(3)}</span>
                      </li>
                    )}
                    {stats.prevDurationSec !== null && (
                      <li>
                        Czas poprzedniej generacji: <span className="font-mono">{stats.prevDurationSec}s</span>
                        {stats.prevDurationSec > 60 && ` (~${Math.round(stats.prevDurationSec / 60)} min)`}
                      </li>
                    )}
                    {stats.prevCostUsd === null && stats.prevDurationSec === null && (
                      <li className="text-muted-foreground italic">Brak danych o poprzedniej generacji</li>
                    )}
                  </ul>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Worker wykona <span className="font-mono">stages={target.stages}, force=true</span>.
                Stara wersja jest <span className="font-medium">trwale</span> nadpisywana — nie ma rollbacku.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus disabled={submitting}>Anuluj</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-600"
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wysylanie...</>
            ) : (
              <>Regeneruj (stracisz powyzsze)</>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
